import { motion, useReducedMotion } from "framer-motion";
import { Logos } from "@/components/Logos";

const HUB = [400, 250];

// The units and mines that feed one monitoring surface.
const UNITS = [
  { code: "DSP", x: 400, y: 90, anchor: "middle", dy: -16 },
  { code: "BSP", x: 556, y: 150, anchor: "start", dy: -12 },
  { code: "ISP", x: 595, y: 286, anchor: "start", dy: 4 },
  { code: "RSP", x: 487, y: 394, anchor: "start", dy: 20 },
  { code: "BSL", x: 313, y: 394, anchor: "end", dy: 20 },
  { code: "MINES", x: 205, y: 286, anchor: "end", dy: 4 },
  { code: "CENTRAL UNITS", x: 244, y: 150, anchor: "end", dy: -12 },
];

const STATS = [
  ["13", "Units & mines"],
  ["₹500 Cr", "Digitalisation mission"],
  ["131", "Officers in training"],
];

const Crosshair = ({ x, y }) => (
  <g stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1.1">
    <line x1={x - 7} y1={y} x2={x + 7} y2={y} />
    <line x1={x} y1={y - 7} x2={x} y2={y + 7} />
  </g>
);

export const SignInHero = () => {
  const still = useReducedMotion();

  return (
    <div
      className="relative hidden overflow-hidden border-r border-zinc-800 bg-zinc-950 lg:block"
      data-testid="sign-in-hero"
    >
      <div className="hero-grid absolute inset-0" aria-hidden="true" />

      <svg
        viewBox="0 0 800 1000"
        preserveAspectRatio="xMidYMin slice"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <filter id="hero-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3.4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="hero-hub-glow">
            <stop offset="0%" stopColor="#FFB347" stopOpacity="0.55" />
            <stop offset="70%" stopColor="#FF6B2C" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#FF6B2C" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="hero-node-glow">
            <stop offset="0%" style={{ stopColor: "var(--acc-pulse)" }} stopOpacity="0.5" />
            <stop offset="100%" style={{ stopColor: "var(--acc-glow)" }} stopOpacity="0" />
          </radialGradient>
        </defs>

        <Crosshair x={60} y={60} />
        <Crosshair x={740} y={60} />
        <Crosshair x={60} y={940} />
        <Crosshair x={740} y={940} />

        <circle cx={HUB[0]} cy={HUB[1]} r="150" fill="url(#hero-hub-glow)" />

        {UNITS.map((u, i) => (
          <g key={u.code}>
            <line
              x1={u.x}
              y1={u.y}
              x2={HUB[0]}
              y2={HUB[1]}
              style={{ stroke: "var(--acc-wire)" }}
              strokeOpacity="0.3"
              strokeWidth="1.2"
            />
            <motion.line
              x1={u.x}
              y1={u.y}
              x2={HUB[0]}
              y2={HUB[1]}
              style={{ stroke: "var(--acc-pulse)" }}
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeDasharray="26 200"
              filter="url(#hero-glow)"
              initial={{ strokeDashoffset: 0, opacity: still ? 0.5 : 1 }}
              animate={still ? undefined : { strokeDashoffset: [0, -226] }}
              transition={{ duration: 4 + i * 0.5, repeat: Infinity, ease: "linear" }}
            />
            <motion.line
              x1={u.x}
              y1={u.y}
              x2={HUB[0]}
              y2={HUB[1]}
              stroke="#FFB347"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeDasharray="10 300"
              filter="url(#hero-glow)"
              initial={{ strokeDashoffset: -150, opacity: still ? 0 : 0.95 }}
              animate={still ? undefined : { strokeDashoffset: [-150, -460] }}
              transition={{ duration: 6 + i * 0.4, repeat: Infinity, ease: "linear" }}
            />
            <circle cx={u.x} cy={u.y} r="26" fill="url(#hero-node-glow)" />
            <rect
              x={u.x - 5}
              y={u.y - 5}
              width="10"
              height="10"
              style={{ fill: "var(--acc-node)" }}
              filter="url(#hero-glow)"
            />
            {!still && (
              <motion.rect
                x={u.x - 11}
                y={u.y - 11}
                width="22"
                height="22"
                fill="none"
                style={{ stroke: "var(--acc-pulse)" }}
                strokeWidth="1.3"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.9, 0] }}
                transition={{ duration: 3.4, repeat: Infinity, delay: i * 0.5, ease: "easeOut" }}
              />
            )}
            <text
              x={u.x}
              y={u.y + u.dy}
              dx={u.anchor === "start" ? 14 : u.anchor === "end" ? -14 : 0}
              textAnchor={u.anchor}
              style={{ fill: "var(--acc-label)" }}
              fillOpacity="0.95"
              className="font-data"
              fontSize="11.5"
              letterSpacing="2.4"
            >
              {u.code}
            </text>
          </g>
        ))}

        <rect
          x={HUB[0] - 18}
          y={HUB[1] - 18}
          width="36"
          height="36"
          fill="#1A0E08"
          stroke="#FFB347"
          strokeWidth="2"
          filter="url(#hero-glow)"
        />
        <rect
          x={HUB[0] - 5}
          y={HUB[1] - 5}
          width="10"
          height="10"
          fill="#FFD08A"
          filter="url(#hero-glow)"
        />
        {!still && (
          <motion.rect
            x={HUB[0] - 34}
            y={HUB[1] - 34}
            width="68"
            height="68"
            fill="none"
            stroke="#FF8A3D"
            strokeWidth="1.6"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.85, 0], scale: [0.85, 1.25, 1.45] }}
            transition={{ duration: 3.8, repeat: Infinity, ease: "easeOut" }}
            style={{ transformOrigin: `${HUB[0]}px ${HUB[1]}px` }}
          />
        )}
        <text
          x={HUB[0]}
          y={HUB[1] + 46}
          textAnchor="middle"
          fill="#FFC46B"
          className="font-data"
          fontSize="11"
          letterSpacing="3"
        >
          SDTD RANCHI
        </text>
      </svg>

      <div
        className="hero-scan hero-scan-tint pointer-events-none absolute inset-x-0 top-0 h-40"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/45 to-transparent" />

      <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
        <div className="flex items-center justify-between gap-6">
          <div className="inline-flex w-fit items-center gap-3 border border-white/10 bg-white/[0.04] p-2 backdrop-blur-md">
            <Logos boxed size="h-11" />
          </div>
          <div
            className="flex items-center gap-2 border border-zinc-800 px-3 py-1.5"
            data-testid="hero-live-badge"
          >
            <span className="relative flex h-1.5 w-1.5">
              {!still && (
                <span className="absolute inline-flex h-full w-full animate-ping bg-emerald-400 opacity-70" />
              )}
              <span className="relative inline-flex h-1.5 w-1.5 bg-emerald-400" />
            </span>
            <span className="font-data text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              Live
            </span>
          </div>
        </div>

        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-data text-[11px] uppercase tracking-[0.3em] text-[color:var(--acc-bright)]"
          >
            SAIL · Pravartanam · SDTD Ranchi
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-zinc-50 sm:text-5xl xl:text-6xl"
          >
            Enterprise-wide
            <br />
            <span className="text-[color:var(--acc-head)]">digital transformation</span>
            <br />
            monitoring.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400"
          >
            One monitoring surface for every digitalisation use case across SAIL — unit by unit,
            stage by stage, from pipeline through trials to go-live, with the benefit each one is
            expected to bring.
          </motion.p>

          <div className="mt-10 grid grid-cols-3 border-t border-zinc-800 pt-6">
            {STATS.map(([v, l], i) => (
              <motion.div
                key={l}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 + i * 0.12 }}
                className={i > 0 ? "border-l border-zinc-800 pl-5" : "pr-5"}
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-data text-3xl font-light tracking-tight text-zinc-100">
                  {v}
                </div>
                <div className="mt-1.5 font-data text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  {l}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
